import { ApiResponse, Entity, Pagination } from '@cervotech/types/api';
import { BehaviorSubject, defer, Observable, of, throwError } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { isNewEntity } from '../helpers';
import { ApiService } from '../services/api.service';

export class CrudService<T extends Entity> {
  selected = new BehaviorSubject<T | undefined | null>(null);
  list: BehaviorSubject<T[]> = new BehaviorSubject<any>([]);

  protected lastQuery?: string;

  private listLoading = new BehaviorSubject<boolean>(false);
  private itemLoading = new BehaviorSubject<boolean>(false);
  private pagination = new BehaviorSubject<Pagination | undefined>(undefined);

  constructor(
    protected apiService: ApiService<T>,
    protected endpoint: string,
    private newEntity: T,
    private responseTransformer: (response: any) => ApiResponse<any> = (
      response
    ) => response
  ) {}

  get listLoading$(): Observable<boolean> {
    return this.listLoading.asObservable();
  }

  get itemLoading$(): Observable<boolean> {
    return this.itemLoading.asObservable();
  }

  get selected$(): Observable<T | undefined | null> {
    return this.selected.asObservable();
  }

  get pagination$(): Observable<Pagination | undefined> {
    return this.pagination.asObservable();
  }

  getMany(query?: string): Observable<T[]> {
    this.lastQuery = query;
    return defer(() => {
      this.listLoading.next(true);
      return this.apiService.get(this.endpoint, query).pipe(
        finalize(() => this.listLoading.next(false)),
        map(this.responseTransformer),
        tap((response) => {
          this.list.next(response.data as T[]);
          this.pagination.next(response.pagination);
        }),
        map((response) => response.data)
      );
    });
  }

  getById(id: T['id'], query?: string): Observable<T> {
    return defer(() => {
      if (isNewEntity(id)) {
        this.selected.next(this.newEntity);
        return of(this.newEntity);
      }
      this.itemLoading.next(true);

      return this.apiService.getOne(id, this.endpoint, query).pipe(
        map(this.responseTransformer),
        map(({ data }) => {
          // Update the selected
          this.selected.next(data);
          // Return the selected
          return data;
        }),
        switchMap((data) => {
          if (!data) {
            return throwError(
              'Could not found selected with id of ' + id + '!'
            );
          }

          return of(data);
        }),
        finalize(() => {
          this.itemLoading.next(false);
        })
      );
    });
  }

  save(item: Partial<T>): Observable<T> {
    return defer(() => {
      this.itemLoading.next(true);
      return this.apiService.save(item, this.endpoint).pipe(
        map(this.responseTransformer),
        map(({ data }) => {
          let saved = data as T;
          const list = this.list.value;
          if (list) {
            // Find the index of the updated selected
            const index = list.findIndex((item) => item.id == saved.id);

            if (!isNewEntity(item) && index > -1) {
              saved = Object.assign(list[index], saved);
              this.list.next(list);
            } else {
              this.list.next([saved, ...list]);
            }
          }

          // Reload items
          this.getMany(this.lastQuery).subscribe();

          this.selected.next(saved);
          return saved;
        }),
        finalize(() => {
          this.itemLoading.next(false);
        })
      );
    });
  }

  delete(id: T['id']): Observable<boolean> {
    return defer(() => {
      this.itemLoading.next(true);
      return this.apiService.deleteOne(id as any, this.endpoint).pipe(
        map(this.responseTransformer),
        map(({ data }) => {
          const isDeleted = data as boolean;
          const list = this.list.value;
          if (list) {
            // Find the index of the deleted selected
            const index = list.findIndex((item) => item.id == id);

            if (index > -1) {
              // Delete the selected
              list.splice(index, 1);

              // Update the entity
              this.list.next(list);
            }
          }

          // Reload items
          this.getMany(this.lastQuery).subscribe();

          this.selected.next(null);

          // Return the deleted status
          return isDeleted;
        }),
        finalize(() => {
          this.itemLoading.next(false);
        })
      );
    });
  }

  getNextItemId() {
    const currentBookId = this.selected.value?.id;
    const items = this.list.value;
    if (!currentBookId) return items[0]?.id;

    const currentBookIndex = items.findIndex(
      (item) => item.id === currentBookId
    );
    const nextBookIndex =
      currentBookIndex + (currentBookIndex === items.length - 1 ? -1 : 1);
    const nextBookId =
      items.length === 1 && items[0].id === currentBookId
        ? null
        : items[nextBookIndex].id;
    return nextBookId;
  }

  clone(): this {
    return new (<any>this)['__proto__'].constructor(
      this.apiService,
      this.endpoint,
      this.newEntity
    ) as this;
  }

  protected execute<P, Q = any>(
    endPoint: string,
    method?: 'POST' | 'GET' | 'PATCH' | 'DELETE',
    body?: Q
  ): Observable<P | undefined> {
    let path = this.endpoint;
    if (this.endpoint) {
      path += `/${endPoint}`;
    }

    return this.apiService.execute<P, Q>(path, method, body).pipe(
      map(this.responseTransformer),
      map((response) => response.data)
    );
  }
}
